import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { SxProps, Theme } from '@mui/material';

export const FIELD_ORIENTATION = {
  ROW: 'row',
  COLUMN: 'column',
} as const;

export const LABEL_VARIANT = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const;

export const ALIGNMENT = {
  START: 'start',
  CENTER: 'center',
  END: 'end',
} as const;

type FieldOrientation = (typeof FIELD_ORIENTATION)[keyof typeof FIELD_ORIENTATION];
type LabelVariant = (typeof LABEL_VARIANT)[keyof typeof LABEL_VARIANT];
type Alignment = (typeof ALIGNMENT)[keyof typeof ALIGNMENT];

interface FormFieldWrapperProps {
  orientation?: FieldOrientation;
  label?: string;
  htmlFor?: string;
  children: ReactNode;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  reserveErrorSpace?: boolean;
  labelAlignment?: Alignment;
  labelVariant?: LabelVariant | string;
  contentAlign?: Alignment;
  sx?: SxProps<Theme>;
}

export default function FormFieldWrapper(props: FormFieldWrapperProps) {
  const {
    orientation = FIELD_ORIENTATION.COLUMN,
    label,
    htmlFor,
    error = false,
    errorMessage,
    helperText,
    reserveErrorSpace = false,
    labelAlignment,
    labelVariant = LABEL_VARIANT.SECONDARY,
    contentAlign = ALIGNMENT.START,
    sx = {},
    children,
  } = props;

  const showErrorMessage = Boolean(error && errorMessage);
  const showHelperText = Boolean(helperText);
  const hasFeedbackRow = showErrorMessage || showHelperText || reserveErrorSpace;

  const justifyMap: Record<string, string> = {
    [ALIGNMENT.START]: 'flex-start',
    [ALIGNMENT.CENTER]: 'center',
    [ALIGNMENT.END]: 'flex-end',
  };

  const labelStyles =
    labelVariant === LABEL_VARIANT.PRIMARY
      ? { variant: 'body1' as const, color: 'text.primary', fontWeight: 500 }
      : { variant: 'body1' as const, color: 'text.primary' };

  const renderLabel = () => (
    <Typography
      {...labelStyles}
      component='label'
      htmlFor={htmlFor}
      sx={{
        whiteSpace: 'nowrap',
        alignSelf:
          labelAlignment ??
          (orientation === FIELD_ORIENTATION.ROW ? ALIGNMENT.CENTER : ALIGNMENT.START),
      }}
    >
      {label}
    </Typography>
  );

  const renderFeedbackRow = () =>
    hasFeedbackRow ? (
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        gap={1}
        sx={{ minHeight: 20 }}
      >
        {showErrorMessage ? <FormHelperText error>{errorMessage}</FormHelperText> : <span />}
        {showHelperText ? (
          <FormHelperText sx={{ color: 'grey.500' }}>{helperText}</FormHelperText>
        ) : null}
      </Stack>
    ) : null;

  if (!label) {
    return (
      <Stack sx={{ width: '100%', ...sx }} gap={0.5}>
        {children}
        {renderFeedbackRow()}
      </Stack>
    );
  }

  if (orientation === FIELD_ORIENTATION.COLUMN) {
    return (
      <Stack sx={{ width: '100%', ...sx }} gap={0.5}>
        {renderLabel()}
        {children}
        {renderFeedbackRow()}
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: '100%', ...sx }} gap={0.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          columnGap: 2,
          width: '100%',
        }}
      >
        {renderLabel()}
        <Stack
          direction='row'
          gap={2}
          alignItems='center'
          justifyContent={justifyMap[contentAlign]}
        >
          {children}
        </Stack>
        <div />
        {renderFeedbackRow()}
      </Box>
    </Stack>
  );
}
